/**
 * The work pipeline's wire layer — Figma pages 04 Development / 05 Deployment.
 *
 * ===========================================================================
 * TRANSCRIBED FROM THE API, AND NOTHING INVENTED
 * ===========================================================================
 * Every shape below is copied from the service that produces it
 * (`apps/api/src/modules/work-orders/work-orders.service.ts`,
 * `work-files/work-files.service.ts`, `lifecycle/lifecycle.service.ts`). Where a
 * field exists on the API and no screen renders it, it is omitted with a reason
 * — the house rule from `features/quotes/api.ts`: a field this module claims to
 * know but never reads is a field that will be wrong the first time somebody
 * trusts it.
 *
 * ===========================================================================
 * THE BOARD'S COLUMNS ARE NOT DECLARED HERE
 * ===========================================================================
 * `WORK_BOARD_COLUMNS` and `columnForWorkOrderStatus` come from `@crf/shared`
 * (ESC-2, owner-ruled and owner-amended: Correction ← `testing_failed`). The API
 * groups with the same function. A local copy in this file is exactly how a
 * column silently under-counts, so there is not one — the server sends the
 * grouping and the web renders what it sends.
 *
 * ===========================================================================
 * MONEY
 * ===========================================================================
 * No work-pipeline response carries a money field EXCEPT the CRF summary on the
 * work-order record, whose `amount` is a registered name in the amounts-masking
 * interceptor. That means a Developer receives `'•••'` in the string itself and
 * this layer needs no special case — it renders what arrived. `work-pipeline`
 * must never re-derive, re-format or arithmetise that string; the masked value
 * is a legitimate value.
 */

import { apiRequest, fetchBinary } from '../../lib/api';
import type { BinaryResponse } from '../../lib/api';
import type { CountryCode } from '../../domain/countries';
import type { WorkBoardColumn, WorkOrderStatus, WorkOrderType } from '@crf/shared';
// `work_type` is NOT in `@crf/shared` — it is a two-member ENUM on the API's `Crf`
// entity — so the web declares it once in the CRF feature and a mirror test holds
// it to the server. See `features/crfs/domain.ts`'s `WORK_TYPES` header.
import type { WorkType } from '../crfs/domain';

/* ────────────────────────────────────────────────────────────── work orders ── */

/** `WorkOrderView` — work-orders.service.ts. */
export interface WorkOrderDto {
  id: string;
  crf_id: string;
  crf_doc_no: string | null;
  /**
   * WHICH KIND OF WORK THE PARENT RECORD IS — `crfs.work_type` (migration 37).
   *
   * ⚠ NULLABLE, AND THE NULL IS THE JOIN'S. Both reads `LEFT JOIN crfs`
   * (`work-orders.service.ts`), so a work order whose CRF is missing reports null
   * rather than a guess, and the API types it that way. The board renders NOTHING
   * for null — see `WorkTypeBadge`'s header on why defaulting it to `'crf'` would
   * be a claim this client has no way to make.
   *
   * ⚠ FREE AT THE QUERY LEVEL: it is one more selected column on a join that was
   * already there, on both the list read and the by-id read. No new round trip and
   * no new index (design §5.1, verified `refute.md` B-1).
   */
  work_type: WorkType | null;
  country_id: number;
  type: WorkOrderType;
  status: WorkOrderStatus;
  assigned_to: string | null;
  /** v22-E2 — the patch job's compile target (migration 59); null otherwise. */
  tally_serial: string | null;
  /** v22-E2 — whether this order's CRF quote carries a `gov_patch` line. */
  is_gov_patch: boolean;
  created_at: string;
}

/** `WorkBoardView` — the whole board, always all five columns, even when empty. */
export interface WorkBoardDto {
  columns: {
    column: WorkBoardColumn;
    label: string;
    count: number;
    cards: WorkOrderDto[];
  }[];
}

/**
 * `POST /work-orders/:id/assign` answers the work order PLUS `notified`.
 *
 * `notified: false` means the assignment stuck and NOBODY WAS TOLD. The API
 * puts it on the wire rather than swallowing it (rule 11), so the UI has to
 * surface it — a silent false here is the same defect one layer up.
 */
export interface AssignResultDto extends WorkOrderDto {
  notified: boolean;
}

export async function fetchWorkBoard(
  countryCode: CountryCode,
  signal?: AbortSignal,
): Promise<WorkBoardDto> {
  return apiRequest<WorkBoardDto>(`/countries/${countryCode}/work-board`, { signal });
}

/**
 * `WorkOrderRecordView` — work-orders.service.ts. The work order, the CRF summary
 * it hangs off, and its testing history, in ONE read.
 *
 * ===========================================================================
 * THE CRF IS NESTED BECAUSE THE SCREEN ALWAYS SHOWS BOTH
 * ===========================================================================
 * Two round trips would let the halves disagree on screen — the work order from
 * one moment, the CRF from another. They are one fact here.
 *
 * `crf.amount` ARRIVES MASKED OR CLEAR, and this layer does not know which. The
 * amounts interceptor reads `crf.country_id` — the nested node's OWN country, not
 * the parent's — and substitutes `'•••'` for a caller without `amounts.view` in
 * that country. So the string may be a number or may be three bullets, and the
 * only correct handling is to RENDER IT. Parsing it, formatting it, or doing
 * arithmetic on it would turn a masked value into `NaN` and put that on screen.
 */
export interface WorkOrderRecordDto extends WorkOrderDto {
  crf: {
    id: string;
    doc_no: string;
    title: string;
    status: string;
    /** Masked (`'•••'`) or clear. Render, never parse. */
    amount: string | null;
    currency: string | null;
    payment_status: PaymentStatus | null;
    country_id: number;
  };
  rounds: {
    id: string;
    round_no: number;
    result: TestResult;
    note: string | null;
    created_at: string;
  }[];
  /**
   * Deployment history and sign-off state.
   *
   * They arrive on THIS read because `lifecycle.controller.ts` is five POSTs and
   * no GET — there is no route that answers "has the developer signed?". A panel
   * built on the write routes alone could offer two buttons and not say whether
   * either had already been pressed.
   */
  deployments: {
    id: string;
    kind: string;
    /** Migration 62 — see `DeploymentDto`. NULL = unrecorded, rendered as an em-dash. */
    mode: string | null;
    due_date: string | null;
    note: string | null;
    deployed_at: string;
  }[];
  /**
   * Each row carries the ACCEPTANCE BLOCK beside its signature (migration 50, F31) —
   * see `SignoffAcceptanceDto`. On a project row exactly one of `acceptance_attachment_id`
   * / `not_required` is set once the F30 gate has run; on a developer row, and on every
   * row signed before migration 50, all seven are null ("not stated"). The panel renders
   * the file name and byte size FROM THIS SNAPSHOT — never by looking the id up in the
   * files list, which is exactly the row the snapshot exists to be immune to.
   */
  signoffs: ({
    id: string;
    type: SignoffType;
    status: 'pending' | 'signed';
    mail_status: MailStatus;
    signed_at: string | null;
  } & SignoffAcceptanceDto)[];
  /**
   * Owner ruling 2 — whether this work order's testing accounts for every point the
   * CRF carries, and WHICH points it does not. See {@link TestPointCoverageDto}.
   *
   * It arrives here for the same reason `deployments` and `signoffs` do, one step
   * further: there is no GET on the lifecycle routes, so nothing else can answer
   * "which requirements still need a verdict?". It is REQUIRED, not optional — the
   * API computes it on every read of this route, and an optional field here would let
   * a fixture omit it and a component quietly render "nothing outstanding" for a work
   * order whose coverage was never asked for.
   */
  coverage: TestPointCoverageDto;
}

export async function fetchWorkOrderRecord(
  workOrderId: string,
  signal?: AbortSignal,
): Promise<WorkOrderRecordDto> {
  return apiRequest<WorkOrderRecordDto>(`/work-orders/${workOrderId}`, { signal });
}

export async function createWorkOrder(crfId: string, type: WorkOrderType): Promise<WorkOrderDto> {
  return apiRequest<WorkOrderDto>(`/crfs/${crfId}/work-orders`, {
    method: 'POST',
    body: { type },
  });
}

export async function assignWorkOrder(
  workOrderId: string,
  userId: string,
  tallySerial?: string,
): Promise<AssignResultDto> {
  return apiRequest<AssignResultDto>(`/work-orders/${workOrderId}/assign`, {
    method: 'POST',
    body: {
      user_id: userId,
      // v22-E2: sent only when supplied — the API refuses a patch job without
      // one, and that refusal must reach the operator verbatim, not be
      // pre-empted by an empty string.
      ...(tallySerial === undefined || tallySerial.trim() === ''
        ? {}
        : { tally_serial: tallySerial.trim() }),
    },
  });
}

/* ──────────────────────────────────────────────────────────────── DL files ── */

/** `WorkFileView` — work-files.service.ts. `mime` is NULL for a TDL, honestly. */
export interface WorkFileDto {
  id: string;
  work_order_id: string;
  file_name: string;
  mime: string | null;
  size_bytes: number;
  created_at: string;
}

/** Figma 12:5281 — "Drop TDL, PDF, ZIP — up to 10 MB". The cap is the API's. */
export const WORK_FILE_MAX_BYTES = 10 * 1024 * 1024;
export const WORK_FILE_ACCEPT = '.tdl,.pdf,.zip';

export async function fetchWorkFiles(
  workOrderId: string,
  signal?: AbortSignal,
): Promise<WorkFileDto[]> {
  return apiRequest<WorkFileDto[]>(`/work-orders/${workOrderId}/files`, { signal });
}

/**
 * Multipart, exactly one part named `file`. The interceptor rejects anything
 * else with a 422 `multipart_shape`, so this must not append a second field —
 * not even a "name" the operator typed, which the server derives itself.
 */
export async function uploadWorkFile(workOrderId: string, file: File): Promise<WorkFileDto> {
  const form = new FormData();
  form.append('file', file, file.name);
  return apiRequest<WorkFileDto>(`/work-orders/${workOrderId}/files`, {
    method: 'POST',
    body: form,
  });
}

/**
 * The BYTES of one attached file (OQ-WF-DOWNLOAD, owner-ruled).
 *
 * Through `fetchBinary`, not a plain `<a href>`: the route is behind the JWT
 * guard, and neither an anchor nor `window.open` carries an `Authorization`
 * header — both would get a 401 that renders as a broken download with no
 * explanation. Going through the client also means this request obeys the same
 * single-flight refresh discipline as every other, so a download started against
 * an expiring token does not fail where a retry would have worked.
 *
 * The server's `Content-Disposition` filename is preferred over the row's
 * `file_name` so the browser saves exactly what the API said it was sending.
 */
export async function downloadWorkFile(
  workOrderId: string,
  fileId: string,
): Promise<BinaryResponse> {
  return fetchBinary(`/work-orders/${workOrderId}/files/${fileId}`);
}

/* ──────────────────────────────────────────────────────────────── lifecycle ── */

export type TestResult = 'in_test' | 'pass' | 'fail';

/**
 * `TEST_POINT_VERDICTS` — test-round-point.entity.ts. THE PER-POINT verdict, which is
 * NOT {@link TestResult}: a round has one result, a point inside it has its own.
 *
 * `not_applicable` is owner fork 18 — the only lever for a requirement the customer
 * withdrew, because a point cannot be cancelled and Cancel CRF was never built. The
 * API makes its remark mandatory, in the DTO and again in a database CHECK.
 */
export type TestPointVerdict = 'fail' | 'pass' | 'not_applicable';

/**
 * `CoverageGap` — test-point-coverage.ts. WHY a point is not accounted for, kept as
 * three causes rather than a count because they call for three different actions:
 * test it, re-test it against the wording that stands, or fix the work first.
 */
export type CoverageGap = 'never_tested' | 'text_changed' | 'failed';

/** One live grid point with the verdict standing against it for this work order. */
export interface CoveragePointDto {
  point_no: number;
  description: string;
  verdict: TestPointVerdict | null;
  remark: string | null;
  round_no: number | null;
  /** Null exactly when this point is accounted for. */
  gap: CoverageGap | null;
}

/**
 * `TestPointCoverageView` — owner ruling 2, "Deploy REQUIRES every test point passed".
 *
 * ⚠ `covered: false` WITH AN EMPTY `outstanding` IS A REAL STATE, NOT A CONTRADICTION.
 * It means the CRF carries NO test points at all — owner fork 3 says such a CRF may
 * not deploy, and there is no number to name. Distinguish it by `points.length === 0`,
 * never by reading `outstanding` alone.
 *
 * `points` is the tester's ENTRY grid as well as the coverage answer, and the two must
 * stay one read: the write echoes each description back and the server refuses a byte
 * that drifted, so a grid fetched separately can produce a refusal for a mismatch this
 * screen never showed.
 *
 * NOTHING HERE GATES ANYTHING YET. The deploy route admits today exactly what it
 * admitted before; this is the answer made visible, one cluster ahead of the refusal.
 */
export interface TestPointCoverageDto {
  covered: boolean;
  points: CoveragePointDto[];
  outstanding: { point_no: number; gap: CoverageGap }[];
}

/**
 * One point's verdict AS THE TESTER SUBMITS IT — `TestRoundPointDto`
 * (`lifecycle/dto/lifecycle.dto.ts`). The request half of {@link CoveragePointDto}.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * `description` IS AN ECHO, AND IT MUST BE THE TEXT THAT WAS ON SCREEN
 * ═══════════════════════════════════════════════════════════════════════════════
 * The server compares this to the live `crf_points` row with `===` and refuses a
 * byte that drifted (`POINT_TEXT_STALE`) — deliberately in TypeScript, because
 * `crf_points` is `utf8mb4_0900_ai_ci` and, measured, `'Column'` and `'column'`
 * compare EQUAL to that database. So this field must carry the description the
 * tester actually read, copied from `coverage.points`, never retyped, re-cased or
 * trimmed. A screen that normalised it would turn a refusal-by-design into a
 * refusal nobody can explain.
 *
 * `point_no` is NAMED rather than inferred from array position — the opposite of
 * the grid writer, which numbers rows 1..N from order. A tester submitting verdicts
 * for points 2 and 5 by position would have them recorded against 1 and 2.
 */
export interface TestRoundPointInput {
  point_no: number;
  description: string;
  verdict: TestPointVerdict;
  remark?: string;
}

/** `TestRoundView` — round_no is ALLOCATED by the server (ESC-27ii); never sent. */
export interface TestRoundDto {
  id: string;
  work_order_id: string;
  round_no: number;
  result: TestResult;
  note: string | null;
  /** Where the work order ended up — the routing decision, on the wire. */
  work_order_status: WorkOrderStatus;
  created_at: string;
}

export interface DeploymentDto {
  id: string;
  work_order_id: string;
  kind: 'deploy' | 'redeploy';
  /**
   * WHICH WAY the software went out / the date it was DUE (migration 62, R8-A).
   * Both NULL = unrecorded — the server's honest value for anything the operator
   * never stated; the panel renders an em-dash, never a guess. `due_date` is a bare
   * 'YYYY-MM-DD' (the server DATE_FORMATs it), so no timezone can shift the day.
   */
  mode: string | null;
  due_date: string | null;
  note: string | null;
  deployed_at: string;
}

export type SignoffType = 'developer' | 'project';
export type MailStatus = 'none' | 'sent' | 'received' | 'acknowledged';

/**
 * THE CUSTOMER'S WRITTEN ACCEPTANCE, as the API returns it — `SignoffView`,
 * lifecycle.service.ts, migration 50 (Train F, owner ruling R2: *"Project sign-off
 * REQUIRES the customer's written acceptance. RESTORE. That is the document that ends a
 * dispute."*).
 *
 * `acceptance_file_name` / `acceptance_byte_size` / `acceptance_sha256` are a SNAPSHOT
 * taken from the stored bytes at sign time, written in the same statement as the
 * signature. They are what was accepted; the attachment row they were copied from can
 * be renamed or replaced later and these do not move (the v11 immunity amendment). So
 * this client renders THEM and never re-derives them from the files list.
 *
 * `not_required` is fork 5's counted escape hatch: `true` with a `not_required_reason`
 * means the signer declared no acceptance was needed and said why. `null` on every field
 * means "not stated" — a developer sign-off, or a project sign-off from before the gate.
 */
export interface SignoffAcceptanceDto {
  acceptance_attachment_id: string | null;
  acceptance_file_name: string | null;
  acceptance_byte_size: number | null;
  acceptance_sha256: string | null;
  acceptance_recorded_at: string | null;
  not_required: boolean | null;
  not_required_reason: string | null;
}

export interface SignoffDto extends SignoffAcceptanceDto {
  id: string;
  crf_id: string;
  type: SignoffType;
  status: 'pending' | 'signed';
  mail_status: MailStatus;
  signed_at: string | null;
}

/**
 * What the signer STATES about the acceptance — the body of `POST /crfs/:id/signoffs/project`
 * beyond `mail_status` (`SignoffDto`, lifecycle.dto.ts). Fork 7: named in the SAME request
 * that signs. Exactly one of:
 *   · `acceptance_attachment_id` — a file already attached to THIS CRF (`GET /crfs/:id/files`);
 *     the server verifies it belongs here and snapshots it;
 *   · `not_required: true` + `not_required_reason` — the hatch, and the reason is MANDATORY.
 * The server refuses everything else by name: nothing stated → 409 naming the acceptance;
 * both at once → 400; the hatch without a reason → 400; a reason without the hatch → 400.
 * ⚠ ON THE DEVELOPER ROUTE THESE ARE REFUSED (400 `wrong_signoff`), not ignored — so this
 * client sends NONE of them there (`useSignOff`).
 */
export interface SignoffAcceptanceBody {
  acceptance_attachment_id?: string;
  not_required?: boolean;
  not_required_reason?: string;
}

export type PaymentStatus = 'advance' | 'pending' | 'received' | 'foc';

/**
 * Record a round, optionally with the grid as this round judged it.
 *
 * ⚠ `points` IS OMITTED, NEVER SENT EMPTY, WHEN THE TESTER JUDGED NOTHING. The API
 * treats `[]` and absent alike today, but an un-upgraded server refuses an unknown
 * property outright — measured on the base commit, `400 VALIDATION_FAILED`,
 * `["property points should not exist"]`, zero rows written. Sending the key
 * unconditionally would therefore break every round recorded against a server that
 * has not been deployed yet, including rounds that carry no per-point evidence at
 * all. The caller decides; this function forwards.
 */
export async function recordTestRound(
  workOrderId: string,
  body: { result: TestResult; note?: string | null; points?: TestRoundPointInput[] },
): Promise<TestRoundDto> {
  return apiRequest<TestRoundDto>(`/work-orders/${workOrderId}/test-rounds`, {
    method: 'POST',
    body,
  });
}

/**
 * `mode` and `due_date` (migration 62) are OMITTED, never sent null, when the operator
 * stated nothing: the server stores NULL because the client SAID nothing, not because
 * the client sent a null it invented — and an un-upgraded server refuses an unknown
 * property outright (the `points` precedent above), so an untouched flow keeps working
 * against it.
 */
export async function recordDeployment(
  workOrderId: string,
  body: {
    kind?: 'deploy' | 'redeploy';
    mode?: 'online' | 'offline';
    due_date?: string;
    note?: string | null;
  },
): Promise<DeploymentDto> {
  return apiRequest<DeploymentDto>(`/work-orders/${workOrderId}/deployments`, {
    method: 'POST',
    body,
  });
}

/**
 * ONE function, two routes, and the body shape is the same on both — but what this
 * client PUTS in it differs by route (see `SignoffAcceptanceBody`): the acceptance
 * fields go on the project sign-off only. `useSignOff` is the single caller and owns
 * that rule; this function transmits what it is given.
 */
export async function signOff(
  crfId: string,
  type: SignoffType,
  body: { mail_status?: MailStatus } & SignoffAcceptanceBody,
): Promise<SignoffDto> {
  return apiRequest<SignoffDto>(`/crfs/${crfId}/signoffs/${type}`, { method: 'POST', body });
}

/**
 * Payment status has its OWN route by owner ruling — it is deliberately not
 * reachable through any general CRF update, so that a commercial fact cannot
 * change as a side effect of renaming a title. This function is the only way
 * this app moves it.
 */
export async function setPaymentStatus(
  crfId: string,
  paymentStatus: PaymentStatus,
): Promise<{ id: string; payment_status: PaymentStatus }> {
  return apiRequest(`/crfs/${crfId}/payment-status`, {
    method: 'POST',
    body: { payment_status: paymentStatus },
  });
}

/* ─────────────────────────────────────────────────────────────── query keys ── */

export const workKeys = {
  board: (cc: CountryCode) => ['work-board', cc] as const,
  record: (workOrderId: string) => ['work-order', workOrderId] as const,
  files: (workOrderId: string) => ['work-files', workOrderId] as const,
};
